import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, distinctUntilChanged, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { User } from '../models/user.model';
import { UserProfileService } from './user-profile.service';

@Injectable({ providedIn: 'root' })
export class PublicBrandingService {
  readonly accountAlias$: Observable<string | null>;
  readonly brandingProfile$: Observable<User | null>;
  private readonly storageKey = 'yachtapp.accountAliasRef';

  constructor(
    private readonly router: Router,
    private readonly userProfileService: UserProfileService
  ) {
    this.accountAlias$ = this.router.routerState.root.queryParamMap.pipe(
      map(params => params.get('ref')?.trim() ?? ''),
      map(alias => this.normalizeAlias(alias)),
      tap(alias => {
        if (alias) {
          this.persistAlias(alias);
        }
      }),
      map(alias => alias ?? this.loadStoredAlias()),
      distinctUntilChanged()
    );

    this.brandingProfile$ = this.accountAlias$.pipe(
      switchMap(alias => (alias ? this.userProfileService.getUserProfileByAccountAlias(alias) : of(null))),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private normalizeAlias(value: string): string | null {
    const normalized = (value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
    return normalized.length ? normalized : null;
  }

  private loadStoredAlias(): string | null {
    try {
      const stored = sessionStorage.getItem(this.storageKey) ?? '';
      const normalized = this.normalizeAlias(stored);
      return normalized;
    } catch {
      return null;
    }
  }

  private persistAlias(alias: string): void {
    try {
      sessionStorage.setItem(this.storageKey, alias);
    } catch {
      // Ignore storage failures (e.g., restricted environments).
    }
  }
}
